import '../styles/editor.css';

import { useEffect, useCallback, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { Sparkles, CheckCircle2 } from 'lucide-react';
import { Text, Flex, Box, Spinner } from '@chakra-ui/react';
import { useEditorStore } from '../store/editorStore';
import AppLayout from '../components/layout/AppLayout';
import { ThemeProvider } from '../context/ThemeContext';
import { loadFontsFromStory } from '../lib/googleFonts';

export default function EditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const loadStory = useEditorStore((s) => s.loadStory);
  const pollStory = useEditorStore((s) => s.pollStory);
  const bootstrapLocalStory = useEditorStore((s) => s.bootstrapLocalStory);
  const saveStory = useEditorStore((s) => s.saveStory);
  const isDirty = useEditorStore((s) => s.isDirty);
  const story = useEditorStore((s) => s.story);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  // AI generation state
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPreparing, setAiPreparing] = useState(false);
  const [aiDone, setAiDone] = useState(false);
  const pollTimer = useRef(null);
  const pollStartTime = useRef(null);
  const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes max

  // Load story on mount
  useEffect(() => {
    if (!id) {
      // navigate('/');
      return;
    }

    if (id === 'new') {
      import('../api/client').then(({ storiesApi }) => {
        storiesApi.create({ title: 'Untitled Story' })
          .then((story) => navigate(`/marketing/story-builder/${story.id}/edit`, { replace: true }))
          .catch(() => {
            bootstrapLocalStory();
            navigate('/marketing/story-builder/local/edit', { replace: true });
          });
      });
      return;
    }

    if (id === 'local') {
      if (!story) bootstrapLocalStory();
      return;
    }

    loadStory(id).catch(() => {
      toast.error('Failed to load story');
      bootstrapLocalStory();
      navigate('/marketing/story-builder/local/edit', { replace: true });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, loadStory, navigate]);

  // Load fonts whenever the story changes (new story or AI-generated pages added)
  useEffect(() => {
    if (story) loadFontsFromStory(story);
  }, [story]);

  // Detect aiGenerating flag and start polling
  useEffect(() => {
    if (!story) return;

    const meta = story.metadata;
    const isGenerating = meta?.aiGenerating === true;
    const hasError = typeof meta?.aiError === 'string';

    if (hasError) {
      toast.error(`AI generation failed: ${meta.aiError}`);
      setAiGenerating(false);
      return;
    }

    if (isGenerating && !aiDone) {
      setAiGenerating(true);

      // Poll every 2 seconds for new pages
      if (!pollTimer.current) {
        pollStartTime.current = Date.now();
        pollTimer.current = setInterval(() => {
          // Timeout after POLL_TIMEOUT_MS
          if (pollStartTime.current && Date.now() - pollStartTime.current > POLL_TIMEOUT_MS) {
            clearInterval(pollTimer.current);
            pollTimer.current = null;
            pollStartTime.current = null;
            setAiGenerating(false);
            toast.error('Story generation timed out. Please try again.');
            return;
          }
          pollStory(id).then(() => {
            const freshMeta = useEditorStore.getState().story?.metadata || {};
            if (!freshMeta.aiGenerating) {
              clearInterval(pollTimer.current);
              pollTimer.current = null;
              pollStartTime.current = null;
              setAiGenerating(false);
              setAiPreparing(true);
              // Full reload + minimum visible delay so the "preparing" status is readable
              const minDelay = new Promise((res) => setTimeout(res, 1800));
              Promise.all([loadStory(id), minDelay])
                .then(() => {
                  setAiPreparing(false);
                  setAiDone(true);
                  setTimeout(() => setAiDone(false), 3000);
                  toast.success('Story generated! Start editing.', { icon: '✨' });
                })
                .catch(() => {
                  setAiPreparing(false);
                  toast.success('Story generated! Start editing.', { icon: '✨' });
                });
            } else if (typeof freshMeta.aiError === 'string') {
              clearInterval(pollTimer.current);
              pollTimer.current = null;
              pollStartTime.current = null;
              setAiGenerating(false);
              toast.error(`AI generation failed: ${freshMeta.aiError}`);
            }
          }).catch(() => {});
        }, 2000);
      }
    } else if (!isGenerating && aiGenerating) {
      setAiGenerating(false);
    }

    return () => {
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.id, story?.metadata?.aiGenerating]);

  // // Autosave every 30 seconds
  // useEffect(() => {
  //   autoSaveTimer.current = setInterval(() => {
  //     if (isDirty) saveStory().catch(() => {});
  //   }, 30_000);
  //   return () => {
  //     if (autoSaveTimer.current) clearInterval(autoSaveTimer.current);
  //   };
  // }, [isDirty, saveStory]);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    const meta = e.metaKey || e.ctrlKey;
    if (meta && e.key === 's') {
      e.preventDefault();
      saveStory().then(() => toast.success('Saved!', { id: 'save' })).catch(() => {});
    }
    if (meta && e.key === 'z') {
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    }
  }, [saveStory, undo, redo]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ── Loading state (Chakra UI) ────────────────────────────────
  if (!story) {
    return (
      <ThemeProvider>
        <Flex w="100%" h="100vh" align="center" justify="center" bg="var(--t-bg)">
          <Box textAlign="center">
            <Spinner color="purple.500" size="lg" mb={3} />
            <Text fontSize="sm" color="gray.500">Loading story editor…</Text>
          </Box>
        </Flex>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      {/* react-hot-toast portal — scoped to the editor */}
      <Toaster
        position="top-right"
        toastOptions={{ style: { fontSize: 13, borderRadius: 8 } }}
      />

      <Box position="relative" w="100%" h="100%">
        <AppLayout />

        {/* AI generation banner */}
        {(aiGenerating || aiPreparing || aiDone) && (
          <Flex
            position="fixed"
            top={4}
            left="50%"
            transform="translateX(-50%)"
            zIndex={50}
            align="center"
            gap={3}
            px={5}
            py={3}
            borderRadius="full"
            fontSize="sm"
            fontWeight="medium"
            color="white"
            transition="all 0.2s"
            bg={
              aiDone
                ? 'linear-gradient(135deg, #10b981, #059669)'
                : aiPreparing
                ? 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary))'
                : 'linear-gradient(135deg, var(--brand-primary), #8b5cf6)'
            }
            style={{
              boxShadow: aiDone
                ? '0 4px 24px rgba(16,185,129,0.4)'
                : aiPreparing
                ? '0 4px 24px rgba(14,165,233,0.45)'
                : '0 4px 24px rgba(99,102,241,0.5)',
            }}
          >
            {aiDone ? (
              <>
                <CheckCircle2 size={16} />
                Story complete! All pages ready to edit.
              </>
            ) : aiPreparing ? (
              <>
                <Spinner size="xs" color="white" speed="0.7s" />
                Preparing edit sandbox…
              </>
            ) : (
              <>
                <Sparkles size={15} className="animate-pulse" />
                AI is building your story
                {story.pages.length > 0 && (
                  <Box
                    as="span"
                    px={2}
                    py="2px"
                    borderRadius="full"
                    fontSize="xs"
                    fontWeight="bold"
                    ml={1}
                    bg="rgba(255,255,255,0.25)"
                  >
                    {story.pages.length}{' '}
                    {story.pages.length === 1 ? 'page' : 'pages'} so far
                  </Box>
                )}
                <Flex gap={1} ml={2}>
                  {[0, 150, 300].map((delay) => (
                    <Box
                      key={delay}
                      w="6px"
                      h="6px"
                      bg="rgba(255,255,255,0.7)"
                      borderRadius="full"
                      className="animate-bounce"
                      style={{ animationDelay: `${delay}ms` }}
                    />
                  ))}
                </Flex>
              </>
            )}
          </Flex>
        )}
      </Box>
    </ThemeProvider>
  );
}
